import { Link } from 'react-router-dom';
import PageShell from '../components/PageShell';
import PolicyLayout from './PolicyLayout';
import {
  ADDRESS,
  BRAND_NAME,
  COMPANY_NAME,
  POLICY_UPDATED,
  SITE_DOMAIN,
  SUPPORT_PHONE_LABEL,
  SUPPORT_PHONE_TEL,
} from '../constants/site';

export default function TermsPage() {
  return (
    <PageShell>
      <PolicyLayout
        title="Terms & Conditions"
        updated={POLICY_UPDATED}
        intro={`When you browse ${SITE_DOMAIN}, phone ${BRAND_NAME}, or chat with one of our technicians, you enter into an agreement with ${COMPANY_NAME} ("we," "our," or "us") on the conditions set out below (the "Terms"). Please look them over first. If any part is unacceptable to you, stop using the site and our services.`}
      >
        <h2>Section 1: Who We Are and What We Offer</h2>
        <p>
          {BRAND_NAME} is a third-party help desk run on an independent basis. Through articles,
          how-to guides, and one-on-one assistance (some free, some paid), we help people with Family
          Tree Maker and connected genealogy tools. Typical topics include setup and activation,
          program updates, syncing trees with online accounts, backup and restore, media trouble,
          TreeVault questions, and everyday troubleshooting on both Windows PCs and Macs.
        </p>

        <h2>Section 2: Our Relationship to Software Publishers</h2>
        <p>
          We did <strong>not</strong> create or publish the software we support. {COMPANY_NAME} has no
          partnership, sponsorship, or endorsement arrangement with Software MacKiev, Ancestry.com,
          FamilySearch, or other brand owners. Trademarked names appear on this site purely so
          readers know which programs we work on.
        </p>
        <p>
          If your product is still under warranty, the publisher may help you at no charge, and
          nothing in these Terms stops you from going to them first.
        </p>

        <h2>Section 3: Age and Authority to Accept</h2>
        <p>
          Buying a service from us requires that you be 18 or older, or that a parent or guardian has
          given consent. You also confirm that what you tell us is truthful and that you are entitled
          to operate the machine and the programs involved in the session.
        </p>

        <h2>Section 4: Getting Ready for a Session</h2>
        <p>A smooth session depends on a few things only you can take care of:</p>
        <ul>
          <li>
            Make a fresh backup of your tree files and linked media before any work begins, and keep
            it somewhere other than the computer being repaired.
          </li>
          <li>Hold a genuine, lawfully purchased license for each program we will work on.</li>
          <li>
            Have the administrator password for the computer on hand, since installs and repairs
            usually need it.
          </li>
          <li>
            Describe the symptoms honestly and tell us which program edition and operating system
            you run.
          </li>
          <li>Shut any private documents, email, or banking windows before screen sharing begins.</li>
        </ul>

        <h2>Section 5: How Remote Help Works</h2>
        <ul>
          <li>Help can reach you over the phone, in chat, by email, or through a screen-sharing tool.</li>
          <li>
            No technician connects to your machine until you say yes, and you may disconnect them the
            moment you choose.
          </li>
          <li>
            Our technician will talk you through each change as it happens and will leave files that
            have nothing to do with your problem untouched.
          </li>
          <li>Please remain at your computer for as long as the connection is active.</li>
        </ul>

        <h2>Section 6: Pricing and Billing</h2>
        <p>
          Many of the articles on this site cost nothing to read. Individual sessions and ongoing
          support plans carry a charge, and the exact amount is discussed with you and accepted by
          you <strong>ahead of</strong> any work. Card payments run through secure outside processors;
          we never hold complete card numbers. Our price list may be updated, but a new price never
          affects a service you have already paid for.
        </p>

        <h2>Section 7: Acceptable Use of the Website</h2>
        <p>
          Use this site only for lawful purposes. Scraping or republishing its content, attempting to
          break into it, or interfering with its operation is not permitted.
        </p>

        <h2>Section 8: Outcomes Are Not Guaranteed</h2>
        <p>
          Our technicians give every case their full effort, yet many factors sit beyond our reach:
          aging hardware, operating system updates, the state of online services run by other
          companies, and damage already present in a tree file. For that reason we can&apos;t promise
          a successful fix in every case, nor can we promise to bring back data that has been lost.
        </p>

        <h2>Section 9: Limits on Our Liability</h2>
        <p>
          To the maximum extent the law permits, {COMPANY_NAME} bears no responsibility for indirect,
          incidental, or consequential harm connected with this site or our services, including lost
          records, lost research, or time you could not use your computer. If we are found liable for
          anything, the most we will owe is what you paid for the specific service at issue.
        </p>

        <h2>Section 10: Site Content and Ownership</h2>
        <p>
          The guides, FAQs, and articles on {SITE_DOMAIN} offer general help rather than
          advice tailored to your setup. Menu labels, features, and system requirements shift from
          one release to the next, so the publisher&apos;s own documentation is the final word. The
          original writing and design on this site are the property of {COMPANY_NAME}, and
          reproducing them requires our written consent.
        </p>

        <h2>Section 11: Outside Websites and Services</h2>
        <p>
          Pages here sometimes mention or link to services run by other companies. We have no say over
          what those services publish, whether they stay online, or how they treat personal data.
          Their own terms apply whenever you use them.
        </p>

        <h2>Section 12: Refunds and Privacy</h2>
        <p>
          Money-back requests for our support work are handled under the{' '}
          <Link to="/refund-policy">Refund Policy</Link>. Software bought from a publisher or retailer
          falls under that seller&apos;s return rules instead. To learn how we treat personal data,
          read our <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>

        <h2>Section 13: Updates to These Terms</h2>
        <p>
          These Terms may be revised as our services evolve. The date beside the page title shows the
          latest version, and continuing to use the site after that date counts as acceptance of the
          revised wording.
        </p>

        <h2>Section 14: Applicable Law</h2>
        <p>
          Wyoming state law, together with applicable United States law, governs these Terms, setting
          aside any choice-of-law principles that would otherwise point to another jurisdiction.
        </p>

        <h2>Section 15: Getting in Touch</h2>
        <p>
          <strong>{COMPANY_NAME}</strong>
          <br />
          Postal address: {ADDRESS}
          <br />
          Support line: <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_LABEL}</a>
          <br />
          Web: {SITE_DOMAIN}
        </p>
      </PolicyLayout>
    </PageShell>
  );
}
